import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDeepLTranslated } from '../../../hooks/useDeepLTranslated';
import { useMoney } from '../../../hooks/useMoney';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Navbar, Icon, Button, Card, PageHeader, SectionHeader, SurchargeNotice,
  TextBox, CheckBox, Counter, RouteEditSection, ShoppingSection, DateTimePicker,
  StickyBottomBar, PriceDisplay, ReservationProgressBar, Spinner,
} from '../../../components/common';
import ShoppingSelectModal from '../../../components/service/ShoppingSelectModal';
import RouteEditModal from '../../../components/service/RouteEditModal';
import BookerInfoCard from '../../../components/service/BookerInfoCard';
import VehicleInfoSection from '../../../components/service/VehicleInfoSection';
import { makeDirectionLabels } from '../../../constants/serviceLabels';
import SERVICE_ICON_MAP from '../../../constants/serviceIconMap';
import { RESERVATION_STEPS, TOTAL_RESERVATION_STEPS } from '../../../constants/reservationSteps';
import { pricingService, waitlistService, userService } from '../../../api/services';
import { useAvailabilityCheck } from '../../../hooks/useAvailabilityCheck';
const hasShoppingSvc = (svcType, _dir) => svcType === 'airport';
const hasFlightInfo = (svcType, dir) => svcType === 'airport' && dir === 'from-airport';

/* Route mode per service type:
   - airport/hospital: free stopovers (2-point + 경유지 추가)
   - golf/ski: fixed waypoint (3-point: 출발지 → 골프장/스키장 → 도착지)
   - pet/business: simple 2-point only */
const simpleRouteTypes = new Set(['pet', 'business']);
const fixedWaypointTypes = new Set(['golf', 'ski']);


export default function BookingSettings() {
  const { t } = useTranslation();
  const money = useMoney();
  const serviceNames = { airport: t('service.airport'), golf: t('service.golf'), ski: t('service.ski'), hospital: t('service.hospital'), pet: t('service.pet'), business: t('service.business') };
  const defaultArrivalLabels = { airport: t('booking.default_arrival_airport'), golf: t('form.destination'), ski: t('form.destination'), hospital: t('form.hospital_location'), pet: t('form.destination'), business: t('form.destination') };
  const { type } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isWaitingMode = searchParams.get('waiting') === 'true';
  const direction = searchParams.get('direction');

  // 언어 전환은 full-reload를 수반하므로(LanguageContext) history state가 소실된다.
  // 예약 페이지에서 언어만 바꿨는데 랜딩으로 튕기지 않도록, history state가 있으면
  // sessionStorage에 formData를 캐시하고, state 누락 시 이 캐시로 복구한다.
  const bookingCacheKey = `vvt_booking_${type || 'airport'}_${direction || 'default'}`;
  const landingData = useMemo(() => {
    if (location.state?.formData) return location.state.formData;
    try {
      const cached = sessionStorage.getItem(bookingCacheKey);
      if (cached) return JSON.parse(cached);
    } catch {}
    return {};
  }, [location.state, bookingCacheKey]);

  useEffect(() => {
    if (location.state?.formData) {
      try { sessionStorage.setItem(bookingCacheKey, JSON.stringify(location.state.formData)); } catch {}
    }
  }, [location.state, bookingCacheKey]);

  // #25: 직접 URL 접근(state/cache 모두 없음) 시에만 랜딩으로 리다이렉트
  useEffect(() => {
    if (location.state?.formData) return;
    let cached = null;
    try { cached = sessionStorage.getItem(bookingCacheKey); } catch {}
    if (!cached) navigate(`/transfer/${type || 'airport'}`, { replace: true });
  }, [location.state, navigate, type, bookingCacheKey]);

  // 결제 페이지로 갔다가 뒤로가기 시 폼 입력값(이름/연락처/옵션/항공편/경유지/주소 등)이
  // 휘발되던 문제 — history entry 별 location.key 로 sessionStorage 에 draft 저장/복원.
  // 새 검색 진입 시 location.key 가 달라 자동으로 fresh state. 결제 완료 시 별도 clear.
  const draftKey = `vvt_booking_draft_${location.key || 'default'}`;

  const { check: checkAvailability, modal: availabilityModal } = useAvailabilityCheck({
    serviceType: type || 'airport',
    dateTime: landingData.dateTime || '',
    passengers: landingData.people || 1,
    onSelectOtherDate: () => navigate(-1),
  });

  /* 마운트 시 가용성 체크 */
  useEffect(() => {
    if (landingData.dateTime) checkAvailability();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── 반려동물 서비스 전용 (pet) ── */
  const [petCount, setPetCount] = useState(1);
  const [petNotes, setPetNotes] = useState('');

  /* ── API-loaded config ── */
  const [serviceOpts, setServiceOpts] = useState([]);
  const [surchargeInfo, setSurchargeInfo] = useState({ overtime: '', overtime_price: 0, stopover_price: 0 });
  const [vehicleData, setVehicleData] = useState({ carName: '', seat: '', capacity: '', luggage: '', usageTimeBadge: null, basePrice: 0, waitInfo: {}, images: [] });
  const [formConfig, setFormConfig] = useState({ bookerFields: [], bookerNotice: '', arrivalLabels: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const svcType = type || 'airport';
    Promise.all([
      pricingService.getServiceOptions(svcType),
      pricingService.getServiceSurcharges(svcType),
      pricingService.getVehicleInfo(svcType),
      pricingService.getBookingFormConfig(),
    ]).then(([opts, sur, veh, cfg]) => {
      setServiceOpts((opts || []).filter((o) => !o.auto).sort((a, b) => {
        // 1차: counter(number) > checkbox — counter 옵션이 위로
        const aCheckbox = a.variant === 'checkbox' ? 1 : 0;
        const bCheckbox = b.variant === 'checkbox' ? 1 : 0;
        if (aCheckbox !== bCheckbox) return aCheckbox - bCheckbox;
        // 2차: counter 옵션 표시 순서 (BugHerd #100)
        // 이용 인원 → 수하물 → 카시트 영아 → 카시트 주니어 → 기타
        const counterRank = (opt) => {
          const code = (opt.code || '').toLowerCase();
          if (code.includes('passenger')) return 1;
          if (code.includes('luggage')) return 2;
          if (code === 'infant_carseat') return 3;
          if (code === 'junior_carseat') return 4;
          if (opt.group === 'carseat') return 3.5; // #185: admin이 만든 카시트 변형도 카시트 그룹 자리로 클러스터
          return 100;
        };
        return counterRank(a) - counterRank(b);
      }));
      setSurchargeInfo(sur || { overtime: '', overtime_price: 0, stopover_price: 0 });
      setVehicleData({ images: [], ...veh });
      setFormConfig({ bookerFields: [], bookerNotice: '', arrivalLabels: {}, ...cfg });
      // init option quantities — draft(뒤로가기 복원)에 값이 있으면 우선 사용
      let _draftOpts = null;
      try {
        const _raw = sessionStorage.getItem(draftKey);
        if (_raw) _draftOpts = JSON.parse(_raw)?.optionQty;
      } catch {}
      const init = {};
      opts.forEach((o) => {
        if (_draftOpts && _draftOpts[o.id] !== undefined) {
          init[o.id] = _draftOpts[o.id];
          return;
        }
        if (o.variant === 'checkbox') {
          init[o.id] = false;
        } else {
          const isPassengers = o.code && o.code.includes('passenger');
          init[o.id] = (isPassengers && landingData.people)
            ? Number(landingData.people)
            : (o.baseQty ?? o.defaultQty ?? 0);
        }
      });
      setOptionQty(init);
      setCurrentBasePrice(veh.basePrice || 0);
      setLoading(false);
    }).catch(() => { setLoading(false); });
  }, [type]);

  /* Direction label per service + direction */
  const directionLabels = makeDirectionLabels(t);
  const directionLabel = directionLabels[direction] || directionLabels[type] || directionLabels.airport;

  /* ---- Form state ---- */
  const [userName, setUserName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [departureDetail, setDepartureDetail] = useState('');
  const [arrivalDetail, setArrivalDetail] = useState('');

  /* 로그인 상태면 프로필에서 예약자 정보 자동 채우기 */
  useEffect(() => {
    if (!localStorage.getItem('accessToken')) return;
    userService.getProfile().then((profile) => {
      if (profile.name && !userName) setUserName(profile.name);
      if (profile.phone && !phone) {
        let ph = profile.phone.replace(/[^\d+]/g, '');
        if (!ph.startsWith('+') && ph.startsWith('0')) ph = '+82' + ph.slice(1);
        setPhone(ph);
      }
      if (profile.email && !email) setEmail(profile.email);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [optionQty, setOptionQty] = useState({});

  /* Flight info (airport from-airport only) */
  const [flightNumber, setFlightNumber] = useState('');
  const [flightArrivalTime, setFlightArrivalTime] = useState('');

  /* Stopovers —
     fixedWaypoints 경로(hospital 왕복, golf, ski)에서는 waypoint의 앞/뒤 둘 다에 경유지를 추가할 수 있음.
     - stopoversBefore: waypoint 앞 (출발지→경유지→병원)
     - stopovers: waypoint 뒤 또는 순수 2지점 경로 (병원→경유지→도착지, airport/pet/business 등) */
  const [stopovers, setStopovers] = useState([]);
  const [stopoversBefore, setStopoversBefore] = useState([]);
  const [stopoverEditId, setStopoverEditId] = useState(null);
  const addStopover = (index) => {
    const newStop = { id: Date.now(), address: '' };
    setStopovers((prev) => {
      const next = [...prev];
      next.splice(typeof index === 'number' ? index : prev.length, 0, newStop);
      return next;
    });
    setStopoverEditId(newStop.id);
  };
  const addStopoverBefore = (index) => {
    const newStop = { id: Date.now() + 1, address: '' };
    setStopoversBefore((prev) => {
      const next = [...prev];
      next.splice(typeof index === 'number' ? index : prev.length, 0, newStop);
      return next;
    });
    setStopoverEditId(newStop.id);
  };
  // id로만 매칭 — 어느 배열에 있든 안전하게 제거/갱신
  const removeStopover = (id) => {
    setStopovers((prev) => prev.filter((s) => s.id !== id));
    setStopoversBefore((prev) => prev.filter((s) => s.id !== id));
  };
  const updateStopover = (id, address, name) => {
    const updater = (prev) => prev.map((s) => s.id === id ? { ...s, address, name: name ?? s.name ?? '' } : s);
    setStopovers(updater);
    setStopoversBefore(updater);
  };

  /* Shopping */
  const [shoppingEnabled, setShoppingEnabled] = useState(false);
  const [shoppingModalOpen, setShoppingModalOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [shoppingCart, setShoppingCart] = useState({});
  const [shoppingTotal, setShoppingTotal] = useState(0);

  /* Route editing — initialized from landing page data */
  const [departure, setDeparture] = useState(landingData.departure || '');
  const [departureName, setDepartureName] = useState(landingData.departureName || '');
  // 왕복: 도착지 명칭도 출발지와 동일 — landingData.destinationName은 병원 이름 등이므로 사용 금지
  const [arrivalName, setArrivalName] = useState(
    landingData.direction === 'round-trip'
      ? (landingData.departureName || '')
      : (landingData.destinationName || '')
  );
  /* golf/ski: venue = fixed middle waypoint (course/resort from landing) */
  const [venueAddress] = useState(
    fixedWaypointTypes.has(type) ? (landingData.venueAddress?.trim() || landingData.venueName || '') : ''
  );
  const [venueName] = useState(
    fixedWaypointTypes.has(type) ? (landingData.venueName || landingData.venueAddress?.trim() || '') : ''
  );
  /* #217: BE Golf.name/Ski.name 정확매칭용 원본 한글명. 번역본(venueName)으로 매칭하면 외국어 진입 시 기본가 폴백. */
  const [venueNameKo] = useState(
    fixedWaypointTypes.has(type) ? (landingData.venueNameKo || '') : ''
  );
  /* golf/ski: arrival = final 도착지 (default to departure for round-trip convenience) */
  const [arrival, setArrival] = useState(() => {
    if (fixedWaypointTypes.has(type)) {
      return landingData.destinationLabel === t('form.departure_same_as') ? landingData.departure : (landingData.destination || landingData.departure || '');
    }
    // 왕복: 도착지 = 출발지
    if (landingData.direction === 'round-trip') return landingData.departure || '';
    return landingData.destination || '';
  });
  const [currentBasePrice, setCurrentBasePrice] = useState(0);
  // #35: BE search 응답의 surcharges_total — holiday/weekend/night 모두 합산된 값
  const [currentSurchargesTotal, setCurrentSurchargesTotal] = useState(0);
  const [editModal, setEditModal] = useState(null); // 'departure' | 'arrival'

  // Draft 복원 — mount 시 1회, 같은 history entry(뒤로가기)에 저장된 값이 있으면 폼에 채움.
  useEffect(() => {
    let d;
    try { const raw = sessionStorage.getItem(draftKey); if (raw) d = JSON.parse(raw); } catch {}
    if (!d) return;
    if (d.userName !== undefined) setUserName(d.userName);
    if (d.phone !== undefined) setPhone(d.phone);
    if (d.email !== undefined) setEmail(d.email);
    if (d.notes !== undefined) setNotes(d.notes);
    if (d.departureDetail !== undefined) setDepartureDetail(d.departureDetail);
    if (d.arrivalDetail !== undefined) setArrivalDetail(d.arrivalDetail);
    if (d.flightNumber !== undefined) setFlightNumber(d.flightNumber);
    if (d.flightArrivalTime !== undefined) setFlightArrivalTime(d.flightArrivalTime);
    if (d.stopovers !== undefined) setStopovers(d.stopovers);
    if (d.stopoversBefore !== undefined) setStopoversBefore(d.stopoversBefore);
    if (d.shoppingEnabled !== undefined) setShoppingEnabled(d.shoppingEnabled);
    if (d.selectedProducts !== undefined) setSelectedProducts(d.selectedProducts);
    if (d.shoppingCart !== undefined) setShoppingCart(d.shoppingCart);
    if (d.shoppingTotal !== undefined) setShoppingTotal(d.shoppingTotal);
    if (d.departure !== undefined) setDeparture(d.departure);
    if (d.departureName !== undefined) setDepartureName(d.departureName);
    if (d.arrival !== undefined) setArrival(d.arrival);
    if (d.arrivalName !== undefined) setArrivalName(d.arrivalName);
    if (d.petCount !== undefined) setPetCount(d.petCount);
    if (d.petNotes !== undefined) setPetNotes(d.petNotes);
    // optionQty 는 API .then() 안에서 init 과 머지(차량옵션이 늦게 로드되므로 거기서 처리).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Draft 저장 — 폼 입력값 변경 시마다 sessionStorage 에 스냅샷.
  useEffect(() => {
    const snap = {
      userName, phone, email, notes, departureDetail, arrivalDetail,
      optionQty, flightNumber, flightArrivalTime,
      stopovers, stopoversBefore,
      shoppingEnabled, selectedProducts, shoppingCart, shoppingTotal,
      departure, departureName, arrival, arrivalName,
      petCount, petNotes,
    };
    try { sessionStorage.setItem(draftKey, JSON.stringify(snap)); } catch {}
  }, [draftKey, userName, phone, email, notes, departureDetail, arrivalDetail,
      optionQty, flightNumber, flightArrivalTime, stopovers, stopoversBefore,
      shoppingEnabled, selectedProducts, shoppingCart, shoppingTotal,
      departure, departureName, arrival, arrivalName, petCount, petNotes]);

  const handleAddressConfirm = useCallback((newAddress, newName) => {
    if (editModal === 'departure') {
      setDeparture(newAddress);
      setDepartureName(newName || '');
      // Row 8: round-trip(병원 왕복 등) — 출발지 변경 시 도착지도 동기화
      if (direction === 'round-trip') {
        setArrival(newAddress);
        setArrivalName(newName || '');
      }
    } else {
      setArrival(newAddress);
      setArrivalName(newName || '');
      // Row 8: round-trip — 도착지 변경 시 출발지도 동기화 (양방향)
      if (direction === 'round-trip') {
        setDeparture(newAddress);
        setDepartureName(newName || '');
      }
    }
    setEditModal(null);
  }, [editModal, direction]);

  /* ---- 설정 변경 시 예상 결제액 재조회 ---- */
  // 결제페이지가 지역가격(ski/golf/hospital 등)을 동일하게 매칭하도록, 마지막으로 사용한
  //   searchPrice body(region 포함)를 저장해 navigate state 로 그대로 넘긴다. (가격 불일치 버그 방지)
  const lastPriceBodyRef = useRef(null);
  useEffect(() => {
    if (loading) return;
    const [datePart, timePart] = (landingData.dateTime || '').split('T');
    const body = { service_type: type };
    if (datePart) body.date = datePart;
    if (timePart) body.pickup_time = timePart;
    /* BugHerd #85: SearchFlow 와 동일하게 address + name 합쳐서 전송.
       다국어 모드에서 address 만 보내면 한국어 시·도·구 키워드를 못 찾아 fallback(198k) 회귀. */
    const joinAddrName = (addr, name) => [addr, name].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(' ').trim();
    const departureCombined = joinAddrName(departure, departureName);
    if (departureCombined) body.departure_region = departureCombined;
    /* BE Golf.name 매칭이 우선이므로 venueName 우선, 미설정 시 venueAddress fallback (BugHerd #38).
       BugHerd #120: hospital 은 HospitalRegion.name(서울/경기/분당) 으로 매칭해야 region 단위 가격이 적용됨.
       기존엔 병원 이름+주소 풀텍스트를 보내 매칭 실패 → fallback 9,900원 회귀.
       SearchFlow.js 와 동일 규칙으로 통일: hospitalRegion(short key) 우선 사용. */
    let pricingDest;
    if (type === 'hospital' && landingData.hospitalRegion) {
      pricingDest = landingData.hospitalRegion;
    } else if (fixedWaypointTypes.has(type)) {
      pricingDest = venueNameKo || venueName || venueAddress;
    } else {
      pricingDest = joinAddrName(arrival, arrivalName);
    }
    if (pricingDest) body.destination_region = pricingDest;
    const passengers = optionQty.people || landingData.people;
    if (passengers) body.passenger_count = Number(passengers);
    const selectedOpts = serviceOpts
      .filter((o) => o.variant === 'checkbox' ? optionQty[o.id] : (optionQty[o.id] || 0) > 0)
      .map((o) => ({ id: o.id, quantity: o.variant === 'checkbox' ? 1 : (optionQty[o.id] || 0) }));
    if (selectedOpts.length) body.selected_options = selectedOpts;
    const totalStopovers = stopovers.length + stopoversBefore.length;
    if (totalStopovers) body.stopover_count = totalStopovers;
    lastPriceBodyRef.current = body; // 결제페이지 재계산이 동일 region 으로 매칭하도록 보관
    pricingService.search(body)
      .then((r) => {
        setCurrentBasePrice(Number(r.base_price ?? 0));
        // #35: BE 가 holiday/weekend/night 할증을 모두 합산해서 surcharges_total 로 반환.
        //      FE 가 자체 재계산하면 평일 공휴일(어린이날 등) 누락. BE 값을 그대로 사용.
        setCurrentSurchargesTotal(Number(r.surcharges_total ?? 0));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, optionQty, stopovers, stopoversBefore, departure, arrival]);

  // 옵션 라벨/extraLabel 다국어 번역 — 백엔드가 한국어로 내려주는 값 치환.
  // unit('명','개')은 DeepL이 오역(명→名 / 개→dog)하므로 i18n 정적 매핑으로 처리.
  const KO_UNIT_I18N = { '명': 'common.people', '개': 'common.unit_count', '대': 'common.unit_vehicle' };
  const translateKoUnit = (u) => {
    if (!u) return '';
    const key = KO_UNIT_I18N[u];
    return key ? t(key) : u;
  };
  const optTextsBundle = useMemo(() => {
    const out = [];
    serviceOpts.forEach((o) => {
      out.push(o.name || '', o.extraLabel || '');
    });
    return out;
  }, [serviceOpts]);
  const tOptTexts = useDeepLTranslated(optTextsBundle);
  // 항공편 정보 섹션의 도착 공항 표기 (departure 한국어를 현재 UI 언어로 번역)
  const tFlightArrivalAirport = useDeepLTranslated(departure);
  const tServiceOpts = serviceOpts.map((o, i) => ({
    ...o,
    nameKo: o.name, // #215: 원본 한글 이름 보존 — 카시트 그룹 판별은 번역 전 한글로(영문에서 부모 누출 방지)
    name: tOptTexts[i * 2] || o.name,
    unit: translateKoUnit(o.unit),
    extraLabel: tOptTexts[i * 2 + 1] || o.extraLabel,
  }));

  /* ---- Computed totals ---- */
  const optionsTotal = serviceOpts.reduce((sum, opt) => {
    if (opt.variant === 'checkbox') return sum + (optionQty[opt.id] ? opt.price : 0);
    const qty = optionQty[opt.id] || 0;
    const baseQty = opt.baseQty ?? opt.base_qty ?? 0;
    const extraQty = Math.max(0, qty - baseQty);
    return sum + opt.price * extraQty;
  }, 0);

  // #56/#59/#91: 공항 외 서비스(golf, ski, hospital, pet, business)는 시간 단위 대절이라 경유지 무료
  // airport는 fixedWaypoints 없음 → stopoversBefore 항상 0, 단 일관성을 위해 합산
  const stopoverPrice = (type === 'airport' && surchargeInfo.stopover_price > 0)
    ? (stopovers.length + stopoversBefore.length) * surchargeInfo.stopover_price
    : 0;
  // #35: BE 가 surcharges_total 로 holiday/weekend/night 합산해서 내려줌.
  //      이전엔 FE 가 자체 재계산하느라 평일 공휴일(예: 5/5 어린이날 = 월요일)
  //      을 토/일 검사로 놓쳐서 어드민 holidays.surcharge_amount 가 무시됐음.
  const totalPrice = currentBasePrice + currentSurchargesTotal + optionsTotal + stopoverPrice + (shoppingEnabled ? shoppingTotal : 0);
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const phoneDigits = phone.replace(/\D/g, '');
  const isValidPhone = phoneDigits.length >= 11;
  const hasRoute = departure.trim().length > 0 && (simpleRouteTypes.has(type) || fixedWaypointTypes.has(type) ? true : arrival.trim().length > 0);
  // 공항 from-airport: 항공편명 + 도착 예정 시간 필수
  const isFromAirport = type === 'airport' && direction === 'from-airport';
  // 항공편명: 영문 2~3자 + 숫자 1~4자 (예: KE821, OZ123, 7C101)
  const isValidFlight = /^[A-Za-z0-9]{2,3}\d{1,4}$/.test(flightNumber.trim());
  const flightInfoFilled = isFromAirport ? (isValidFlight && flightArrivalTime.length > 0) : true;
  const canSubmit = hasRoute && flightInfoFilled && userName.trim().length > 0 && isValidPhone && email.trim().length > 0 && isValidEmail;

  const handleShoppingConfirm = (cart, products, total) => {
    setShoppingCart(cart);
    setSelectedProducts(products);
    setShoppingTotal(total);
    setShoppingModalOpen(false);
  };

  const buildBookingData = () => {
    // 다음 화면(결제/완료)으로 넘길 때부터 이미 번역된 라벨/단위를 담아
    // OptionsSection에서 DeepL 재번역(명→名, 개→dog) 오류가 다시 발생하지 않도록 한다.
    const selectedOpts = tServiceOpts
      .filter((opt) => opt.variant === 'checkbox' ? optionQty[opt.id] : (optionQty[opt.id] || 0) > 0)
      .map((opt) => ({
        name: opt.name,
        value: opt.variant === 'checkbox' ? t('complete.select') : `${optionQty[opt.id]}${opt.unit || ''}`,
        price: opt.variant === 'checkbox' ? opt.price : opt.price * Math.max(0, (optionQty[opt.id] || 0) - (opt.baseQty ?? 0)),
      }));

    return {
      dateTime: landingData.dateTime || '',
      reserverName: userName,
      phone,
      email,
      options: selectedOpts,
      shoppingProducts: shoppingEnabled ? selectedProducts.map((p) => ({
        // BugHerd #194: shopping_id 누락으로 ShoppingOrder 저장이 실패하던 회귀. id 포함 필수.
        id: p.id, brand: p.category, name: p.name, price: p.price, qty: p.qty, image_url: p.image_url,
      })) : [],
      departure,
      departureName,
      departureDetail,
      departureTime: (landingData.dateTime || '').replace('T', ' '),
      departureNotes: notes,
      arrival,
      arrivalDetail,
      destinationName: arrivalName || landingData.destinationName || '',
      // waypoint 앞/뒤 경유지 머지 — 경로 순서대로 (before → after)
      stopovers: [...stopoversBefore, ...stopovers]
        .filter((s) => s.address && s.address.trim())
        .map((s) => ({ name: s.name || '', address: s.address })),
      venueAddress: fixedWaypointTypes.has(type) ? venueAddress : undefined,
      venueName: fixedWaypointTypes.has(type) ? (landingData.venueName || venueAddress) : undefined,
      venueNameKo: fixedWaypointTypes.has(type) ? (venueNameKo || undefined) : undefined,
      flightNumber,
      flightArrivalTime,
      direction,
      subtotal: currentBasePrice,
      originalPrice: totalPrice,
      // #pet: 반려동물 서비스 전용 정보
      petCount: type === 'pet' ? petCount : undefined,
      petNotes: type === 'pet' ? petNotes : undefined,
    };
  };

  const [submitting, setSubmitting] = useState(false);

  // 예약 시간이 이미 지났는지 체크
  const isExpiredTime = landingData.dateTime ? new Date(landingData.dateTime.replace('T', ' ')) <= new Date() : false;

  const handleSubmit = async () => {
    if (isExpiredTime) {
      alert(t('booking.alert_expired'));
      return;
    }
    if (!isWaitingMode) {
      const available = await checkAvailability();
      if (!available) return;
    }
    setSubmitting(true);
    try {
      const bookingData = buildBookingData();

      if (isWaitingMode) {
        /* 대기 등록: /api/waitlist 호출 (예약 생성 X, 결제 X) */
        const waitlistResult = await waitlistService.create({
          service_type: type,
          desired_date: landingData.dateTime ? landingData.dateTime.split('T')[0] : null,
          desired_time: landingData.dateTime ? landingData.dateTime.split('T')[1] : null,
          passengers: optionQty.people || 4,
          departure,
          destination: fixedWaypointTypes.has(type) ? venueAddress : arrival,
          direction: direction || null,
          contact_phone: phone || null,
          contact_email: email || null,
        });
        navigate(`/transfer/${type}/complete?waiting=true`, { state: { bookingData, waitlistId: waitlistResult.id } });
      } else {
        /* 일반 예약: 결제 페이지로 이동만 — 실제 reservation 생성은 결제 시점에 쿠폰 코드와 함께 호출 */
        // 선택 옵션을 OptionInput 형태로 변환
        const optionInputs = serviceOpts
          .filter((o) => o.variant === 'checkbox' ? optionQty[o.id] : (optionQty[o.id] || 0) > 0)
          .map((o) => ({ option_id: o.id, quantity: o.variant === 'checkbox' ? 1 : (optionQty[o.id] || 1) }));
        // 경유지를 StopoverInput 형태로 변환 — before + after 순서대로 머지
        // 골프/스키: 골프장/스키장을 stopovers[0]에 넣고, destination_address에는 실제 도착지
        const mergedStops = [...stopoversBefore, ...stopovers];
        let stopoverInputs = mergedStops.map((s, i) => ({ address: s.address || s, sort_order: i }));
        if (fixedWaypointTypes.has(type) && venueAddress) {
          // golf/ski: venueAddress를 stopoversBefore 뒤, stopovers 앞에 삽입 (실제 경로 순서)
          const beforeCount = stopoversBefore.length;
          const beforeInputs = stopoverInputs.slice(0, beforeCount).map((s, i) => ({ ...s, sort_order: i }));
          const afterInputs = stopoverInputs.slice(beforeCount).map((s, i) => ({ ...s, sort_order: beforeCount + 1 + i }));
          stopoverInputs = [...beforeInputs, { address: venueAddress, sort_order: beforeCount }, ...afterInputs];
        }

        const [, pickupTimePart] = (landingData.dateTime || '').split('T');
        // Row 40: 주소 + 명칭 함께 표기 (예: "서울 강남구 삼성로 212 은마아파트")
        // 명칭이 이미 주소에 포함되어 있거나 동일한 경우 중복 방지
        const _combineAddr = (addr, name) => {
          const a = (addr || '').trim();
          const n = (name || '').trim();
          if (!n || a.includes(n) || a === n) return a;
          return `${a} ${n}`;
        };
        const reservationPayload = {
          service_type: type,
          direction: direction || null,
          // 주소와 상세(메모)는 분리하여 저장 — BE 스키마의 departure_memo/destination_memo 필드 사용
          // (기존 `${address} ${detail}` 로 합쳐 보내던 방식은 조회 시 출발지/도착지 필드가 뒤섞이는 원인이었음)
          departure_address: _combineAddr(departure, departureName),
          departure_memo: departureDetail || null,
          destination_address: _combineAddr(arrival || departure, arrivalName || departureName),
          destination_memo: arrivalDetail || null,
          // 실제 청구(BE create)도 지역가격을 동일 매칭하도록 검색에 쓴 region 동봉 — 미동봉 시
          //   create 가 주소 폴백으로 default base 가 되어 display(지역가격)와 청구액이 어긋남(ski 등).
          departure_region: lastPriceBodyRef.current?.departure_region || undefined,
          destination_region: lastPriceBodyRef.current?.destination_region || undefined,
          pickup_date: landingData.dateTime || null,
          pickup_time: pickupTimePart || null,
          passenger_count: optionQty.people || 4,
          booker_name: userName || null,
          booker_phone: phone || null,
          booker_email: email || null,
          options: optionInputs,
          stopovers: stopoverInputs,
          flight_number: flightNumber || null,
          flight_time: flightArrivalTime || null,
          special_requests: notes || null,
          // BugHerd #194: 쇼핑 항목을 reservation create 와 통합 트랜잭션으로 전송.
          // 이전엔 별도 createOrder API 호출이라 total_price 합산 누락 → PG 결제 시 운송비만 청구 회귀.
          shopping_items: shoppingEnabled && shoppingCart
            ? Object.entries(shoppingCart)
                .filter(([, qty]) => Number(qty) > 0)
                .map(([shopping_id, qty]) => ({ shopping_id: Number(shopping_id), quantity: Number(qty) }))
            : [],
        };
        // #pet: 반려동물 정보는 sessionStorage 에도 백업 — Complete 페이지가 서버 응답 기반이라 유실 방지
        if (type === 'pet') {
          try {
            sessionStorage.setItem('pet_booking_info', JSON.stringify({ petCount, petNotes }));
          } catch (_) {}
        }
        navigate(`/transfer/${type}/payment`, { state: { bookingData: { ...bookingData, reservationPayload, priceSearchBody: lastPriceBodyRef.current } } });
      }
    } catch (err) {
      if (isWaitingMode && (err.response?.status === 409 || err.response?.data?.detail?.includes?.('Already'))) {
        alert(t('booking.alert_already_waiting'));
      } else {
        alert(isWaitingMode ? t('booking.alert_waiting_failed') : t('booking.alert_reservation_failed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />

      {/* Page bg: Figma #F8F9FC, max-width 800px */}
      <main className="bg-mono-50 min-h-screen pb-32 sm:pb-28">
        {loading ? (
          <div className="flex justify-center py-20"><Spinner /></div>
        ) : (
        <div className="max-w-form mx-auto px-2.5 sm:px-5 py-5 flex flex-col gap-1 sm:gap-5">

          {/* ===== Header: Back + Title ===== */}
          <PageHeader title={t('booking.title')} />

          {/* ===== Section 1: 차량 정보 ===== */}
          <Card>
            <SectionHeader variant="card" title={t('booking.vehicle_info')} />
            <VehicleInfoSection
              images={vehicleData.images}
              carName={vehicleData.carName}
              usageTimeBadge={vehicleData.usageTimeBadge}
              specs={[
                { icon: 'van', text: vehicleData.seat },
                { icon: 'person', text: vehicleData.capacity },
                { icon: 'bag', text: vehicleData.luggage },
              ]}
              waitInfo={vehicleData.waitInfo}
              /* 할증은 시스템에서 자동 계산, 차량정보 섹션에 표시 안 함 */
              reviewTitle={vehicleData.reviewTitle}
              reviewRating={vehicleData.reviewRating}
              priceNote={vehicleData.priceNote}
            />
          </Card>

          {/* ===== Section 2: 예약자 정보 ===== */}
          <BookerInfoCard
            userName={userName} setUserName={setUserName}
            phone={phone} setPhone={setPhone}
            email={email} setEmail={setEmail}
            emailError={email.length > 0 && !isValidEmail ? t('booking.alert_invalid_email') : ''}
            phoneError={phone.length > 0 && !isValidPhone ? t('booking.alert_invalid_phone') : ''}
          />

          {/* ===== Section 2.5: 반려동물 정보 (pet 서비스 전용) ===== */}
          {type === 'pet' && (
            <Card>
              <SectionHeader variant="card" title={t('booking.pet_info_title')} />
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <span className="text-2sm font-medium text-mono-600">{t('booking.pet_count_label')}</span>
                  <div className="flex items-center gap-2">
                    <Counter value={petCount} onChange={setPetCount} min={1} max={6} unit={t('booking.pet_unit')} />
                  </div>
                </div>
                <TextBox
                  variant="textarea"
                  value={petNotes}
                  onChange={(e) => setPetNotes(e.target.value)}
                  placeholder={t('booking.pet_notes_placeholder')}
                  rows={3}
                  maxLength={500}
                />
              </div>
            </Card>
          )}

          {/* ===== Section 3: 선택 옵션 ===== */}
          <Card>
            <SectionHeader variant="card" title={t('booking.selected_options')} />
            {(() => {
              // #185: 카시트 그룹 — "카시트"(부모)는 비선택 헤더, 영아용/주니어용(변형)만 선택(nested).
              //  · 부모 판별: 코드 carseat·child_seat / 원본 한글 이름이 정확히 "카시트"
              //  · 변형 판별: group==='carseat' / 코드(infant_carseat·junior_carseat) / 원본 한글 이름에 "카시트" 포함(부모 제외)
              // #215: 영문/일/중/스페인 전환 시 BE가 옵션명을 번역해 내려줘(name="Car seats" 등)
              //       이름 기반 부모 판별이 실패→others로 누출돼 카시트가 중복 노출되던 문제.
              //       1순위: BE가 번역 전 한글로 판정해 보내는 carseat_parent 플래그(전 로케일 일관).
              //       폴백: code(carseat/child_seat) / nameKo(ko 한정). nameKo는 비-ko에선 번역명이라 ko만 커버.
              const koName = (o) => (o.nameKo || o.name || '').trim();
              const isCarseatParent = (o) =>
                o.carseat_parent === true || o.code === 'carseat' || o.code === 'child_seat' || koName(o) === '카시트';
              const isCarseatVariant = (o) =>
                o.group === 'carseat'
                || o.code === 'infant_carseat' || o.code === 'junior_carseat'
                || (koName(o).includes('카시트') && koName(o) !== '카시트');

              const opts = tServiceOpts || [];
              const carseatParent = opts.find(isCarseatParent) || null;
              const carseatVariants = opts.filter((o) => o !== carseatParent && isCarseatVariant(o));
              // 변형이 있을 때만 부모를 비선택 헤더로 처리. 변형 없으면 부모는 일반 선택 옵션으로 둠
              // (단독 "카시트"가 헤더만 뜨고 선택 불가가 되는 것 방지).
              const carseatGrouped = carseatVariants.length > 0;
              const headerParent = carseatGrouped ? carseatParent : null;
              const others = opts.filter((o) => o !== headerParent && !carseatVariants.includes(o));

              const renderOptItem = (opt, nested = false) => (
                opt.variant === 'number' ? (
                  <div key={opt.id} className={`flex flex-col gap-1.5 ${nested ? 'pl-4' : ''}`}>
                    {/* 체크박스 옵션과 동일 양식: 이름(900) + 단가( +N원 )(400) 이름 옆 + 설명 block(500) */}
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-1 flex-wrap min-w-0">
                        <span className="text-2sm font-medium text-mono-900 break-keep break-words">{opt.name}</span>
                        {opt.price > 0 && (
                          <span className="text-2sm font-medium text-mono-500 shrink-0">{`( +${money(opt.price)} )`}</span>
                        )}
                      </span>
                      {opt.extraLabel && (
                        <span className="block text-[15px] sm:text-[13px] font-normal text-mono-600 mt-0.5 leading-relaxed break-keep break-words whitespace-pre-line">{opt.extraLabel}</span>
                      )}
                    </span>
                    <div className="flex items-center gap-2">
                      <Counter
                        value={optionQty[opt.id]}
                        onChange={(v) => setOptionQty((prev) => ({ ...prev, [opt.id]: v }))}
                        min={opt.min}
                        max={(() => {
                          // BE가 max를 보내면 우선, 없으면 옵션 종류로 보정 (어드민에서 직접 조정 가능)
                          // #209: max_qty가 base 이하(어드민이 가격단계 수로 저장 → 보통 1)면 카운터가
                          //       base에 갇혀 + 가 안 눌림. base 초과 유효 cap 일 때만 신뢰, 아니면 fallback.
                          // #267: max_qty 는 가격단계(tier) 수 → 단일단가(tier=1)면 카운터가 1에 갇힘. 1 초과만 실제 cap 으로 신뢰.
                          if (typeof opt.max === 'number' && opt.max > 1 && opt.max > (opt.baseQty ?? 0)) return opt.max;
                          const tag = `${opt.id || ''} ${opt.code || ''} ${opt.name || ''}`.toLowerCase();
                          if (tag.includes('infant_carseat') || tag.includes('영아')) return 1;
                          if (tag.includes('junior_carseat') || tag.includes('주니어')) return 2;
                          if (tag.includes('carseat') || tag.includes('child_seat') || tag.includes('child seat') || tag.includes('카시트')) return 3;
                          if (tag.includes('wheelchair') || tag.includes('휠체어')) return 1;
                          if (tag.includes('passenger') || tag.includes('people') || tag.includes('인원')) return 6;
                          return undefined;
                        })()}
                        unit={opt.unit}
                      />
                      {(() => {
                        const qty = optionQty[opt.id];
                        const isBase = opt.baseQty != null && qty <= opt.baseQty;
                        const chargeAmount = opt.price * Math.max(0, qty - (opt.baseQty ?? 0));
                        const chargeActive = !isBase && opt.price > 0 && qty > 0;
                        // #209/#210 + 옵션 표시 통일: "추가요금(>0) 있을 때만" ( +N원 ) 노출.
                        // 기본 수량 범위(isBase)거나 무료(+0)면 아무것도 표시하지 않음.
                        if (chargeAmount <= 0) return null;
                        return (
                          <span className={`text-2sm font-medium ${chargeActive ? 'text-orange-500' : 'text-mono-500'}`}>
                            {`( +${money(chargeAmount)} )`}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                ) : (
                  <div key={opt.id} className={nested ? 'pl-4' : 'flex flex-col gap-0.5'}>
                    <CheckBox
                      label={`${opt.name}${opt.auto ? ` (${t('booking.auto_applied')})` : ''}`}
                      extraLabel={opt.extraLabel}
                      /* opt는 tServiceOpts 요소라 name/extraLabel 이미 번역됨 */
                      /* 표시 통일: 무료(+0) 옵션은 금액 숨김, 추가요금(>0)만 ( +N원 ) 노출 */
                      charge={opt.price > 0 ? `( +${money(opt.price)} )` : null}
                      chargeActive={!!optionQty[opt.id]}
                      checked={!!optionQty[opt.id]}
                      onChange={() => setOptionQty((prev) => ({ ...prev, [opt.id]: !prev[opt.id] }))}
                    />
                  </div>
                )
              );

              return (
                <>
                  {others.map((opt) => renderOptItem(opt))}
                  {carseatGrouped && (
                    <div className="flex flex-col gap-1.5">
                      {/* #185: 카시트(부모) = 비선택 헤더 + 도로교통법 문구. 영아용/주니어용만 선택(nested). */}
                      {/* 옵션 라벨과 동일 양식: 제목 위 + 안내 문구 block 하단. 모바일 안전(break). */}
                      <div className="mt-2 min-w-0">
                        <span className="flex items-center gap-1 flex-wrap min-w-0">
                          <span className="text-2sm font-medium text-mono-900 break-keep break-words">{t('booking.carseat_group', '카시트 추가')}</span>
                        </span>
                        {/* 안내 문구 = 부모 "카시트" 옵션 문구(admin 편집), 없으면 도로교통법 기본. 괄호 없이 하단 노출. */}
                        <span className="block text-sm sm:text-xs font-normal text-mono-500 mt-0.5 leading-relaxed break-keep break-words whitespace-pre-line">
                          {headerParent?.extraLabel || t('booking.carseat_law_notice', '도로교통법상 만 6세(72개월) 미만은 카시트 착용 의무')}
                        </span>
                      </div>
                      {carseatVariants.map((opt) => renderOptItem(opt, true))}
                    </div>
                  )}
                </>
              );
            })()}
            {/* 주말/공휴일 할증 표기는 #55/#110 요청으로 제거 — 시스템 상에선 가격 계산에 그대로 포함됨 */}
          </Card>

          {/* ===== Section 4: 프라이빗 쇼핑 서비스 (airport only) ===== */}
          {hasShoppingSvc(type, direction) && (
            <Card>
              <SectionHeader variant="card" title={t('booking.private_shopping_section')} />

              {/* #257: (i) 아이콘·볼릿(•) 제거, 첫 줄 SemiBold, 항목 간 여백(8px)·line-height 1.5.
                  볼릿은 자동생성이 아니라 i18n 텍스트의 리터럴 '•' → 텍스트에서 제거함. */}
              <div className="border border-mono-200 rounded-lg p-3 flex flex-col gap-2">
                {t('booking.private_shopping_desc').split('\n').map((ln, i) => (
                  <p key={i} className={`text-sm sm:text-2sm break-keep break-words leading-[1.5] ${i === 0 ? 'font-semibold text-mono-800' : 'font-normal text-mono-500'}`}>{ln}</p>
                ))}
              </div>

              {/* Enable checkbox */}
              <CheckBox
                label={t('booking.private_shopping_label')}
                charge={shoppingEnabled && shoppingTotal > 0 ? `( +${money(shoppingTotal)} )` : null}
                chargeActive={shoppingEnabled && shoppingTotal > 0}
                checked={shoppingEnabled}
                onChange={() => {
                  const next = !shoppingEnabled;
                  setShoppingEnabled(next);
                  if (next && selectedProducts.length === 0) {
                    setShoppingModalOpen(true);
                  }
                }}
              />
              {/* 3일 전 예약 안내 — international_notice 와 디자인 통일 (#11) */}
              <p
                className="text-2sm font-medium text-orange-500 break-keep"
                dangerouslySetInnerHTML={{ __html: t('info.shopping_3day_notice') }}
              />

              {/* Selected items summary */}
              {shoppingEnabled && selectedProducts.length > 0 && (
                <ShoppingSection
                  showTitle={false}
                  items={selectedProducts.map((p) => ({
                    brand: p.category,
                    name: p.name,
                    price: `${money(p.price)}`,
                    qty: p.qty,
                    // BugHerd #88: 선택 품목 카드에 어드민 업로드 이미지 노출
                    image_url: p.image_url,
                  }))}
                  onEdit={() => setShoppingModalOpen(true)}
                />
              )}

              {/* Shopping total - cart icon + price */}
              {shoppingEnabled && selectedProducts.length > 0 && (
                <div className="flex items-center gap-2 pt-2">
                  <div className="relative">
                    <Icon name="cart" size={28} className="text-mono-900" />
                    <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] bg-orange-500 text-white text-2xs font-bold rounded-full flex items-center justify-center px-0.5 leading-none">
                      {selectedProducts.reduce((sum, p) => sum + p.qty, 0)}
                    </span>
                  </div>
                  <span className="text-xl font-bold text-mono-900">{money(shoppingTotal)}</span>
                </div>
              )}

              {/* Open shopping modal button */}
              {shoppingEnabled && selectedProducts.length === 0 && (
                <button
                  onClick={() => setShoppingModalOpen(true)}
                  className="w-full h-12 border border-mono-200 rounded-lg text-sm font-bold text-mono-900 hover:bg-mono-50 transition"
                >
                  {t('booking.select_products')}
                </button>
              )}
            </Card>
          )}

          {/* ===== Section 5: 항공편 정보 (공항→도착지 only) ===== */}
          {hasFlightInfo(type, direction) && (
            <Card>
              <SectionHeader variant="card" title={t('booking.flight_info')} />
              <div className="flex gap-2 border border-mono-200 rounded-lg p-2">
                <Icon name="info" size={16} className="text-mono-500 shrink-0 mt-0.5" />
                <p className="flex-1 min-w-0 text-sm sm:text-xs font-normal text-mono-500 leading-[19.2px] break-keep break-words">
                  {t('booking.flight_notice')}
                </p>
              </div>
              <div className="flex items-start gap-4">
                <Icon name="airport" size={28} className="text-mono-900 shrink-0 mt-1" />
                <div className="flex-1 space-y-3">
                  <div>
                    <span className="text-2sm font-medium text-mono-600">{t('booking.flight_arrival_airport')}</span>
                    <p className="text-sm font-bold text-mono-900">{tFlightArrivalAirport || '-'}</p>
                  </div>
                  <div>
                    <TextBox
                      label={<>{t('booking.flight_number')} <span className="text-orange-500">*</span></>}
                      placeholder={t('booking.flight_number_placeholder')}
                      value={flightNumber}
                      onChange={(e) => setFlightNumber(e.target.value.toUpperCase())}
                      className="w-full lg:max-w-[280px]"
                    />
                    <p className="text-xs sm:text-2xs text-mono-400 mt-1">{t('booking.flight_number_hint')}</p>
                  </div>
                  <div>
                    <span className="text-2sm font-medium text-mono-600 block mb-1.5">{t('booking.flight_arrival_time')} <span className="text-orange-500">*</span></span>
                    <div className="lg:max-w-[280px]">
                      <DateTimePicker
                        value={flightArrivalTime}
                        onChange={setFlightArrivalTime}
                      />
                      <p className="text-xs sm:text-2xs text-mono-400 mt-1">{t('booking.flight_arrival_time_hint')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* ===== Section 6: 이동 정보 ===== */}
          <Card>
            <RouteEditSection
              directionLabel={directionLabel}
              departure={{ name: departureName, address: departure, time: landingData.dateTime, icon: (direction?.startsWith('from-') && type !== 'airport') ? (SERVICE_ICON_MAP[type] || 'van') : 'van' }}
              /* 왕복: 도착지는 출발지 데이터를 그대로 재사용, label만 "(출발지와 동일)"로 표시 */
              arrival={direction === 'round-trip' ? {
                label: t('booking.destination_round_trip'),
                name: departureName,
                address: departure,
                icon: 'place',
              } : {
                label: direction?.startsWith('from-') || fixedWaypointTypes.has(type) || simpleRouteTypes.has(type) ? t('form.destination') : ((formConfig.arrivalLabels && formConfig.arrivalLabels[type]) || defaultArrivalLabels[type] || t('form.destination')),
                name: arrivalName || landingData.destinationName || '',
                address: arrival,
                icon: direction?.startsWith('from-') || fixedWaypointTypes.has(type) || simpleRouteTypes.has(type) ? 'place' : (SERVICE_ICON_MAP[type] || 'place'),
              }}
              /* Fixed waypoints for golf/ski (3-point route) or hospital round-trip */
              {...(fixedWaypointTypes.has(type) ? {
                fixedWaypoints: [{
                  id: 'venue',
                  label: type === 'golf' ? t('form.golf_course') : t('form.ski_resort'),
                  address: venueName || venueAddress || t('booking.venue_address_placeholder'),
                  detail: venueAddress && venueName !== venueAddress ? venueAddress : '',
                  icon: SERVICE_ICON_MAP[type] || 'place',
                }],
              } : direction === 'round-trip' ? {
                fixedWaypoints: [{
                  id: 'hospital-wp',
                  label: t('form.hospital_location'),
                  address: landingData.destination || landingData.venueName || '',
                  icon: 'hospital',
                }],
              } : {})}
              /* Free stopovers — 모든 서비스에서 경유지 추가 가능 (#56/#59/#91)
                 - airport: stopover_price만큼 유료
                 - 그 외 (golf/ski/hospital/pet/business): 시간 단위 대절이라 무료
                 - fixedWaypoints 경로(hospital 왕복, golf, ski): waypoint 앞/뒤 둘 다 추가 가능 */
              {...{
                stopovers,
                onAddStopover: addStopover,
                onRemoveStopover: removeStopover,
                onEditStopover: (id) => setStopoverEditId(id),
                stopoversBefore,
                onAddStopoverBefore: addStopoverBefore,
                onRemoveStopoverBefore: removeStopover,
                onEditStopoverBefore: (id) => setStopoverEditId(id),
                stopoverCharge: type === 'airport'
                  ? `+${money(surchargeInfo.stopover_price)}`
                  : t('booking.stopover_free'),
              }}
              notes={notes}
              onNotesChange={(e) => setNotes(e.target.value)}
              departureDetail={departureDetail}
              onDepartureDetailChange={
                type === 'airport' && direction === 'from-airport'
                  ? undefined
                  : (e) => setDepartureDetail(e.target.value)
              }
              arrivalDetail={arrivalDetail}
              onArrivalDetailChange={
                type === 'airport' && direction === 'to-airport'
                  ? undefined
                  : (e) => setArrivalDetail(e.target.value)
              }
              onEditDeparture={() => setEditModal('departure')}
              onEditArrival={() => setEditModal('arrival')}
            />
          </Card>

          {/* ===== Section 6: 현장 추가 요금 안내 — overtime_price(raw 숫자) 우선 사용해 useMoney 환산 =====
              BugHerd #230: 공항은 "초과 대기 요금" (overtime_waiting_fee_per_hour), 그 외 서비스는
              "시간 초과 요금" (overtime_fee_per_hour). pricing.js getVehicleInfo / SearchFlow / VehicleInfoSection
              은 이미 분기되어 있는데 본 SurchargeNotice 만 무조건 'overtime_fee_per_hour' 사용하던 누락. */}
          <SurchargeNotice
            title={surchargeInfo.surchargeTitle}
            text={(surchargeInfo.overtime_price && Number(surchargeInfo.overtime_price) > 0)
              ? t(type === 'airport' ? 'booking.overtime_waiting_fee_per_hour' : 'booking.overtime_fee_per_hour',
                  { amount: money(Number(surchargeInfo.overtime_price)) })
              : surchargeInfo.overtime}
          />

        </div>
        )}
      </main>

      {/* ===== Sticky bottom bar ===== */}
      <StickyBottomBar step={RESERVATION_STEPS.booking} totalSteps={TOTAL_RESERVATION_STEPS}>
        {isExpiredTime && (
          <p className="text-2xs text-red-500 text-center mb-1">{t('booking.expired_warning')}</p>
        )}
        <div className="flex items-center justify-between w-full">
          <PriceDisplay label={t('booking.estimated_payment')} amount={totalPrice} layout="stack" approx />
          <Button onClick={handleSubmit} width="fixed-140" disabled={!canSubmit || submitting || isExpiredTime}>{submitting ? t('booking.processing') : t('booking.book_now')}</Button>
        </div>
      </StickyBottomBar>

      {/* Shopping modal */}
      {shoppingModalOpen && (
        <ShoppingSelectModal
          onClose={() => setShoppingModalOpen(false)}
          onConfirm={handleShoppingConfirm}
          initialCart={shoppingCart}
        />
      )}

      {/* Route edit modal (departure / arrival) */}
      {editModal && (
        <RouteEditModal
          type={type}
          field={editModal}
          direction={direction}
          currentValue={editModal === 'departure' ? departure : arrival}
          onConfirm={handleAddressConfirm}
          onClose={() => setEditModal(null)}
        />
      )}

      {availabilityModal}

      {/* Stopover edit modal — before/after 양쪽에서 검색 */}
      {stopoverEditId && (
        <RouteEditModal
          type={type}
          field="stopover"
          departure={departure}
          arrival={arrival}
          currentValue={[...stopoversBefore, ...stopovers].find((s) => s.id === stopoverEditId)?.address ?? ''}
          onConfirm={(newAddress, newName) => {
            const trimmed = newAddress.trim();
            if (trimmed && (trimmed === departure.trim() || trimmed === arrival.trim())) {
              alert(t('booking.alert_stopover_same'));
              return;
            }
            const allStops = [...stopoversBefore, ...stopovers];
            const otherStopovers = allStops.filter((s) => s.id !== stopoverEditId);
            if (trimmed && otherStopovers.some((s) => s.address?.trim() === trimmed)) {
              alert(t('booking.alert_stopover_dup'));
              return;
            }
            updateStopover(stopoverEditId, newAddress, newName || '');
            setStopoverEditId(null);
          }}
          onClose={() => {
            // 빈 경유지(주소 미입력) 제거 — 양쪽 배열에서
            const keep = (s) => s.id !== stopoverEditId || (s.address && s.address.trim());
            setStopovers((prev) => prev.filter(keep));
            setStopoversBefore((prev) => prev.filter(keep));
            setStopoverEditId(null);
          }}
        />
      )}
    </>
  );
}
